/**
 * Route guards — client-side replacement for Next.js middleware.ts
 *
 * AuthGuard: protects dashboard routes (redirects to /login if not authenticated)
 * GuestGuard: protects auth routes (redirects to /dashboard if already authenticated)
 *
 * Auth state is stored in localStorage via XState (authListMachine),
 * hydrated on mount. These guards use the XState hooks to read it.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useIsAuthenticated, useAuthLoading } from '@repo/auth'
import { Loader } from '@repo/ui'

export function AuthGuard() {
  const { pathname } = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const isLoading = useAuthLoading()

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader className="h-8 w-8" />
      </div>
    )
  }

  if (!isAuthenticated) {
    const loginUrl = `/login?redirect=${encodeURIComponent(pathname)}`
    return <Navigate to={loginUrl} replace />
  }

  return <Outlet />
}

export function GuestGuard() {
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const isLoading = useAuthLoading()

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader className="h-8 w-8" />
      </div>
    )
  }

  if (isAuthenticated) {
    const params = new URLSearchParams(location.search)
    const redirectTo = params.get('redirect') || '/dashboard'
    return <Navigate to={redirectTo} replace />
  }

  return <Outlet />
}
